// b20 (PL7, ändrad 23/10): namnen tvättas innan de lämnar navet, och igen i molnet. Ett namn i
// Home Assistant eller Homey kan bära mer än ett namn: Arlo-kontots enheter heter "Aarlo for
// <e-postadress>", Verisures centralenhet heter som husets gatuadress, och en Zigbee-adapter har
// ett hårdvaru-id inom parentes. Regeln, som navet och molnet delar:
//   - "<märke> for <e-post>" blir "<märke>-kontot", till exempel "Arlo-kontot". En e-postadress i
//     ett annat namn tas bort;
//   - ett namn som ser ut som en gatuadress (ett gatunamn med nummer, eller kommaseparerat med ett
//     nummer) ersätts med integrationens namn, till exempel "Verisure-anläggningen";
//   - hårdvaru-id tas bort, också inom parentes: MAC-adresser, UUID, följder av minst 8
//     hexadecimala tecken med en siffra (eller 12 utan), och ord med minst 8 siffror;
//   - telefonnummer (minst 8 siffror, med +, mellanslag, bindestreck eller parentes) tas bort;
//   - b20-1: ett MAC-slut, ett avslutande ord med exakt 6 hexadecimala tecken med både siffror och
//     bokstäver a–f efter -, _ eller ett mellanslag, tas bort: "home-assistant-voice-7c3e2b" blir
//     "home-assistant-voice" (ESPHome och Voice PE sätter slutet av MAC-adressen i namnet).
// Ett vanligt namn, som "Entre", "Ute temp", "Smart Lock Pro" eller "Bärbar 1", står kvar, och
// också "decade", "123456", "Gen4" och "SNZB-02WD".
// Regeln är medvetet försiktig åt rätt håll: hellre ett tvättat namn för mycket än en adress för
// lite.

import type { Fynd } from "./fynd.ts";

const EPOST = /[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}/g;
const KONTO = /^\s*(.+?)\s+(?:for|för|de|von|pour)\s+[A-Za-z0-9._%+-]+@[^\s]+\s*$/i;
const UUID = /[0-9A-Fa-f]{8}-(?:[0-9A-Fa-f]{4}-){3}[0-9A-Fa-f]{12}/g;
const MAC = /(?<![0-9A-Za-z])[0-9A-Fa-f]{2}(?:[:-][0-9A-Fa-f]{2}){5,7}(?![0-9A-Za-z])/g;
/** En följd av hexadecimala tecken som är ett id: minst 8 med en siffra, 12 utan, eller 16. */
const HEX = /(?<![0-9A-Za-z])(?:0x)?[0-9A-Fa-f]{8,}(?![0-9A-Za-z])/g;
const arHexId = (s: string) => {
  const h = s.replace(/^0x/i, "");
  return /\d/.test(h) || h.length === 12 || h.length >= 16;
};
/** Ett telefonnummer: + eller en siffra, och sedan siffror, mellanslag, bindestreck och parenteser. */
const TELEFON = /(?:\+|\b00|\(|\b)\d[\d\s().-]{6,}\d/g;
const siffror = (s: string) => (s.match(/\d/g) ?? []).length;

/** b20-1: ett MAC-slut i slutet av ett namn, efter -, _ eller ett mellanslag. */
const MAC_SLUT = /[-_\s]([0-9A-Fa-f]{6})$/;

/** Namnet utan ett avslutande MAC-slut: 6 hexadecimala tecken med både siffror och bokstäver. */
export function utanMacSlut(s: string): string {
  const t = s.trimEnd();
  const m = MAC_SLUT.exec(t);
  const slut = m?.[1];
  return slut && /\d/.test(slut) && /[a-f]/i.test(slut) ? t.slice(0, m.index) : s;
}

/** Gatunamn som sammansatta ord med nummer: Storgatan 5, Kungsvägen 12B, Bahnhofstraße 3. */
const GATA_SAMMANSATT =
  /\p{L}{2,}(?:gatan|gata|gate|gaten|vägen|väg|veien|vei|vejen|vej|gränd|grand|torget|backen|stigen|leden|allén|straße|strasse|gasse|weg|platz|laan|straat)\s+\d+\s?[A-Za-z]?\b/iu;
/** Engelska: 1 Main Street, 22B Baker St. */
const GATA_ENGELSKA =
  /\b\d+\s?[A-Za-z]?\s+(?:[\p{L}'.-]+\s+){1,3}(?:street|st|road|rd|avenue|ave|lane|ln|drive|dr|boulevard|blvd|way|court|ct|place|terrace|crescent|close)\b\.?/iu;
/** Spanska, katalanska, franska och portugisiska: Calle Mayor 12, Avda. del Mar, 4. */
const GATA_FORE =
  /\b(?:calle|c\/|avenida|avda|av|plaza|pza|paseo|camino|carrer|carretera|ctra|urbanizaci[oó]n|urb|rua|rue|chemin|boulevard|piazza)\.?\s+[\p{L}\s.'-]{1,60}?,?\s*(?:n[º°o.]?\s*)?\d+/iu;
/** Kommaseparerat med ett husnummer mellan två delar: "<gata>, 14, <postnummer och ort>". */
const KOMMA_NUMMER = /^[^,]{3,},\s*\d{1,4}\s?[A-Za-z]?\s*,\s*\S/u;
/** Ett postnummer med en ort efter ett kommatecken: "…, 29680 Estepona", "…, 114 55 Stockholm". */
const POSTNUMMER = /,\s*(?:[A-Z]{1,2}-?)?\d{3}\s?\d{2}\s+\p{L}{2,}/u;

/** Ser namnet ut som en gatuadress? */
export function arAdress(namn: string): boolean {
  return (
    GATA_SAMMANSATT.test(namn) ||
    GATA_ENGELSKA.test(namn) ||
    GATA_FORE.test(namn) ||
    KOMMA_NUMMER.test(namn) ||
    POSTNUMMER.test(namn)
  );
}

/** Integrationens namn i ord, till exempel verisure → Verisure, securitas_direct → Securitas. */
const INTEGRATIONSNAMN: Record<string, string> = {
  verisure: "Verisure",
  securitas_direct: "Securitas",
  securitas: "Securitas",
  yale_smart_alarm: "Yale",
  ajax: "Ajax",
  alarmo: "Alarmo",
  aarlo: "Arlo",
  arlo: "Arlo",
  homey: "Homey",
};

export function integrationsnamn(integration: string | null | undefined): string | null {
  if (!integration) return null;
  const n = INTEGRATIONSNAMN[integration];
  if (n) return n;
  if (!/^[a-z0-9_]{2,40}$/.test(integration)) return null;
  const ord = integration.split("_")[0] ?? integration;
  return `${ord[0]?.toUpperCase() ?? ""}${ord.slice(1)}`;
}

/** Märket i "Aarlo for …": Aarlo (HACS) är Arlo. */
const markeFor = (m: string) => (/^a?arlo$/i.test(m.trim()) ? "Arlo" : m.trim());

/** Det som blir kvar när en del är borttagen: tomma parenteser, ord och skiljetecken i kanterna. */
function stada(s: string): string {
  let ut = s;
  for (let i = 0; i < 3; i++) {
    ut = ut
      .replace(/\(\s*[,;:/-]*\s*\)|\[\s*[,;:/-]*\s*\]|<\s*>/g, " ")
      .replace(/\s+(?:for|för|de|von|pour|-|–|—|:|,|;|\/|\||#)\s*$/i, "")
      .replace(/^\s*(?:-|–|—|:|,|;|\/|\||#)\s*/, "")
      .replace(/\s{2,}/g, " ")
      .trim();
  }
  return ut;
}

export interface Tvattval {
  /** Integrationen, för namnet när ett namn är en adress. */
  integration?: string | null;
  /** Vad en adress blir: integrationens anläggning (förval), eller null (till exempel för ett rum). */
  adress?: "anlaggning" | "ingen";
}

/**
 * Tvättar ett namn från e-post, adresser, hårdvaru-id och telefonnummer. Null när inget namn blir
 * kvar, eller när namnet var null.
 */
export function tvattaNamn(namn: string | null | undefined, val: Tvattval = {}): string | null {
  if (typeof namn !== "string") return null;
  const n = namn.trim();
  if (!n) return null;
  const konto = KONTO.exec(n);
  if (konto?.[1]) {
    const m = stada(tvattaNamn(markeFor(konto[1])) ?? "");
    return m ? `${m}-kontot` : "Kontot";
  }
  if (arAdress(n)) {
    if (val.adress === "ingen") return null;
    const i = integrationsnamn(val.integration);
    return i ? `${i}-anläggningen` : "Anläggningen";
  }
  const ut = stada(
    n
      .replace(EPOST, " ")
      .replace(UUID, " ")
      .replace(MAC, " ")
      .replace(HEX, (s) => (arHexId(s) ? " " : s))
      .replace(TELEFON, (s) => (siffror(s) >= 8 ? " " : s))
      // Ett ord med minst 8 siffror, till exempel ett serienummer.
      .replace(/\S+/g, (s) => (siffror(s) >= 8 ? " " : s)),
  );
  // b20-1: och ett MAC-slut i slutet, när resten är tvättat.
  return stada(utanMacSlut(ut)) || null;
}

/**
 * En modell eller tillverkare: bara e-post, MAC-adresser, UUID och långa hexadecimala id tas bort,
 * så att katalogen fortfarande känner igen modellen.
 */
export function tvattaModell(v: string | null | undefined): string | null {
  if (typeof v !== "string") return null;
  const ut = stada(
    v
      .replace(EPOST, " ")
      .replace(UUID, " ")
      .replace(MAC, " ")
      .replace(HEX, (s) => (s.replace(/^0x/i, "").length >= 12 && arHexId(s) ? " " : s)),
  );
  return ut || null;
}

/** Innehåller texten något som tvätten tar bort? För proven och migreringen. */
export const behoverTvatt = (namn: string | null | undefined, val: Tvattval = {}) =>
  typeof namn === "string" && tvattaNamn(namn, val) !== namn.trim();

/**
 * En text, till exempel en rad i platsens logg: e-post, MAC-adresser, UUID, hårdvaru-id,
 * telefonnummer och ord med minst 8 siffror tas bort. En adress i en mening känns inte igen här;
 * migreringen byter de kända namnen först.
 */
export function tvattaText(text: string): string {
  return text
    .replace(EPOST, "…")
    .replace(UUID, "…")
    .replace(MAC, "…")
    .replace(HEX, (s) => (arHexId(s) ? "…" : s))
    .replace(TELEFON, (s) => (siffror(s) >= 8 ? "…" : s))
    .replace(/\S+/g, (s) => (siffror(s) >= 8 ? "…" : s))
    .replace(/\(\s*…\s*\)/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/**
 * Ett fynd med tvättade namn, rum och modeller (Home Assistant och Homey). Fälten som fanns finns
 * kvar, också som null: ett nav från b18 känns igen på att namnet finns (skiljerRegn).
 */
export function tvattaFynd(f: Fynd): Fynd {
  const ut: Fynd = { ...f };
  if (f.ha) {
    const h = { ...f.ha };
    h.tillverkare = tvattaModell(h.tillverkare);
    h.modell = tvattaModell(h.modell);
    if ("namn" in h) h.namn = tvattaNamn(h.namn, { integration: h.integration });
    if ("rum" in h) h.rum = tvattaNamn(h.rum, { adress: "ingen" });
    if ("via" in h) h.via = tvattaModell(h.via);
    ut.ha = h;
  }
  if (f.homey) {
    const h = { ...f.homey };
    if ("namn" in h) h.namn = tvattaNamn(h.namn, { integration: "homey" });
    if ("rum" in h) h.rum = tvattaNamn(h.rum, { adress: "ingen" });
    ut.homey = h;
  }
  return ut;
}
